'use client';

import React, { useEffect, useRef, useState, useTransition } from 'react';
import { useLocale, useTranslations } from 'next-intl';
import { useRouter } from 'next/navigation';
import { LOCALE_COOKIE, isLanguageEnabled, type Locale } from '../lib/locale';

/**
 * Compact language switcher for the EscapeTo header (English / Español).
 *
 * Language is INDEPENDENT of country (see app/lib/locale.ts): it toggles the UI
 * chrome language only, via the NEXT_LOCALE cookie, and leaves the URL (and the
 * country selection) untouched. Setting the cookie + router.refresh() re-renders
 * the server tree with the new locale so SSR/metadata pick it up, and the cookie
 * persists the choice for the next load.
 *
 * Gated to escapeto (isLanguageEnabled) — camping-australia never renders this
 * and stays English-only.
 */

const LOCALE_META: Record<Locale, { flag: string; short: string }> = {
  // 🇬🇧 English / 🇪🇸 Español
  en: { flag: '\u{1F1EC}\u{1F1E7}', short: 'EN' },
  es: { flag: '\u{1F1EA}\u{1F1F8}', short: 'ES' },
};

const ORDER: Locale[] = ['en', 'es'];

export default function LanguageSwitcher() {
  const t = useTranslations('language');
  const activeLocale = (useLocale() as Locale) || 'en';
  const router = useRouter();
  const [open, setOpen] = useState(false);
  const [isPending, startTransition] = useTransition();
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClickOutside = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [open]);

  // Belt-and-braces: never render for a brand without the language axis.
  if (!isLanguageEnabled()) return null;

  const label = (loc: Locale) => (loc === 'en' ? t('english') : t('spanish'));

  const switchTo = (next: Locale) => {
    setOpen(false);
    if (next === activeLocale) return;
    // One-year cookie; the layout reads it server-side (i18n/request.ts).
    document.cookie = `${LOCALE_COOKIE}=${next}; path=/; max-age=31536000; samesite=lax`;
    // Re-render server components with the new locale (updates <html lang>,
    // metadata and the message catalogue) without a hard reload.
    startTransition(() => router.refresh());
  };

  const active = LOCALE_META[activeLocale] || LOCALE_META.en;

  return (
    <div ref={rootRef} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`${t('label')}: ${label(activeLocale)}. ${t('change')}`}
        disabled={isPending}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          padding: '6px 10px',
          borderRadius: '9999px',
          border: '1px solid #e5e7eb',
          background: '#fff',
          fontSize: '13px',
          fontWeight: 600,
          color: '#374151',
          cursor: 'pointer',
          lineHeight: 1,
          opacity: isPending ? 0.6 : 1,
        }}
      >
        <span aria-hidden style={{ fontSize: '15px' }}>{active.flag}</span>
        <span>{active.short}</span>
        <span aria-hidden style={{ fontSize: '9px', color: '#9ca3af' }}>▼</span>
      </button>

      {open && (
        <div
          role="listbox"
          aria-label={t('label')}
          style={{
            position: 'absolute',
            top: '100%',
            right: 0,
            marginTop: '4px',
            minWidth: '150px',
            background: '#fff',
            border: '1px solid #e5e7eb',
            borderRadius: '10px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
            zIndex: 1000,
            padding: '4px 0',
          }}
        >
          {ORDER.map((loc) => (
            <button
              key={loc}
              type="button"
              role="option"
              aria-selected={loc === activeLocale}
              onClick={() => switchTo(loc)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                width: '100%',
                padding: '8px 14px',
                border: 'none',
                background: loc === activeLocale ? '#f3f4f6' : 'transparent',
                fontSize: '13px',
                fontWeight: loc === activeLocale ? 700 : 500,
                color: '#374151',
                cursor: 'pointer',
                textAlign: 'left',
              }}
            >
              <span aria-hidden style={{ fontSize: '15px' }}>{LOCALE_META[loc].flag}</span>
              <span>{label(loc)}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
